import * as Yup from 'yup';

import { QuoteProductTruckingInfo } from '@core/types/api/QuoteProductTruckingInfo/QuoteProductTruckingInfo';

import { Currency, CurrencyWireFormat } from '../types/api/Currency/Currency';
import Enums from '../types/api/Enums';
import type { Product } from '../types/api/Product/Product';
import type { ProjectProduct } from '../types/api/ProjectProduct/ProjectProduct';
import { NewQuoteProduct, type QuoteProduct } from '../types/api/QuoteProduct/QuoteProduct';
import { EmptyTenantDefaultMargins, TenantDefaultMargins } from '../types/tenantDefaultMargins';
import { CalculateTargetMarginCurrency } from '../utils/costCalculations';
import { CurrencyString, NullableCurrencyString } from '../utils/currency';
import { resolveCurrencyCode } from '../utils/format';
import { FormikCurrency, FormikDeliveryCosts } from '../utils/formikHelpers';
import { CalculateWithDeliveryTotalPrice } from '../utils/withDeliveryPriceCalculations';
import {
  YupCurrency,
  YupDecimal,
  YupDeliveryCosts,
  YupEnum,
  YupNullableCurrency,
  YupNullableDecimal,
  YupNullableEnum,
  YupNullableNumber,
  YupNullablePercentage,
  YupNullableReference,
  YupNullableString,
  YupReference,
  type YupSchemaDeliveryCostsType,
  type YupSchemaNullableCurrencyType,
  type YupSchemaNullableReferenceType,
  type YupSchemaReferenceType,
  nonNegativeCurrency,
} from '../utils/yupHelpers';

export type WithDeliveryDisplayOnlyType = {
  withDeliverySubtotalPrice: string | null;
  withDeliveryTotalPrice: string | null;
  haulMargin: string | null;
};

export type AdditionalTruckingInfoFormikType = {
  truckingType: YupSchemaReferenceType;
  haulRate: YupSchemaNullableCurrencyType;
  minimumHaulCharge: ProjectProduct['minimumHaulCharge'] & QuoteProduct['minimumHaulCharge'];
  taxRate: ProjectProduct['taxRate'] & QuoteProduct['taxRate'];
  haulCost: YupSchemaNullableCurrencyType;
  haulTaxable: ProjectProduct['haulTaxable'] & QuoteProduct['haulTaxable'];
  // Delivery Pricing-displayOnly fields
  withDeliveryDisplayOnly: WithDeliveryDisplayOnlyType | null;
};

export type AdditionalTruckingInfoEditableFieldsFormikType = Omit<
  AdditionalTruckingInfoFormikType,
  'withDeliveryDisplayOnly'
>;

const AdditionalTruckingInfosSchema = Yup.array().of(
  Yup.object({
    truckingType: YupReference('TruckingType'),
    haulRate: YupNullableCurrency('Haul rate'),
    minimumHaulCharge: YupNullableNumber({ label: 'Minimum haul charge' }),
    taxRate: YupNullablePercentage('Tax rate', 2).test({
      name: 'taxRate-must-not-exceed-999.99',
      test: (item) => (item ?? '') === '' || parseFloat(item ?? '0') <= 999.99,
      message: 'Must not exceed 999.99',
    }),
    haulCost: YupNullableCurrency('Haul cost'),
    haulTaxable: Yup.bool().default(false),
  }),
);

/**
 * ProjectOrQuoteProductFormikType is a shared formik type that contains data
 * required for both ProjectProduct and QuoteProduct.
 */
export type ProjectOrQuoteProductFormikType = {
  // Fields shared between ProjectProduct and QuoteProduct
  externalName: ProjectProduct['externalName'] | QuoteProduct['externalName'];
  usage: ProjectProduct['usage'] | QuoteProduct['usage'];
  quantity: ProjectProduct['quantity'] | QuoteProduct['quantity'];
  price: CurrencyWireFormat;
  originalPrice?: CurrencyWireFormat | null;

  // QuoteProduct-specific fields
  kind: QuoteProduct['kind'];
  discounts?: QuoteProduct['discounts'];
  class: QuoteProduct['class'];

  // Fields shared between ProjectProduct and QuoteProduct, with different form vs domain types
  product: YupSchemaReferenceType & {
    incompatibleMixQuantityUnits: boolean;
    allowIncompatibleMixQuantityUnits: boolean;
    category?: Enums.ProductCategory;
    nonTaxable?: boolean;
  };
  deliveryCosts: YupSchemaDeliveryCostsType;

  // Manual form-state lever for toggling between money and percentage display.
  // Specifically out of displayOnly to avoid recalculation logic.
  isMoneySwitchOn: boolean;

  // Delivery Pricing-specific fields
  haulTaxable: ProjectProduct['haulTaxable'] & QuoteProduct['haulTaxable'];
  deliveryType: ProjectProduct['deliveryType'] & QuoteProduct['deliveryType'];
  haulRate: YupSchemaNullableCurrencyType;
  minimumHaulCharge: ProjectProduct['minimumHaulCharge'] & QuoteProduct['minimumHaulCharge'];
  taxRate: ProjectProduct['taxRate'] & QuoteProduct['taxRate'];
  truckingType: YupSchemaNullableReferenceType;
  haulCost: YupSchemaNullableCurrencyType;

  withDeliveryDisplayOnly: WithDeliveryDisplayOnlyType | null;

  // Display-only formik fields
  displayOnly: {
    suggestedPrice: string | null;
    listPrice: string | null;
    unitCost: string | null;
    actualMarginPercentage: string | null;
    actualMarginCurrency: string | null;
    marginOverMaterialsPercentage: string | null;
    marginOverMaterialsCurrency: string | null;
    targetMargin: string | null;
    targetMarginPercentage: string | null;
    targetMarginCurrency: string | null;
  } | null;

  additionalTruckingInfos: AdditionalTruckingInfoFormikType[];
};

export type ProjectOrQuoteProductEditableFieldsFormikType = Omit<
  ProjectOrQuoteProductFormikType,
  'displayOnly' | 'withDeliveryDisplayOnly'
> & {
  additionalTruckingInfos: AdditionalTruckingInfoEditableFieldsFormikType[];
};

/**
 * MainProjectOrQuoteProductsFormikSchema is the formik schema for the main product
 * section of a Quote or Project. It is used to validate the form data.
 * It operates properly for Primary products.
 */
export const MainProjectOrQuoteProductsFormikSchema: Yup.SchemaOf<
  ProjectOrQuoteProductEditableFieldsFormikType[]
> = Yup.array().of(
  Yup.object({
    product: YupReference('Product', {
      incompatibleMixQuantityUnits: Yup.bool().default(false),
      allowIncompatibleMixQuantityUnits: Yup.bool().default(false),
      category: YupNullableEnum(Enums.ProductCategory, 'Category'),
      nonTaxable: Yup.bool().default(false),
    }).test({
      name: 'products have incompatible mix batch units',
      test: (item) => !item.incompatibleMixQuantityUnits || item.allowIncompatibleMixQuantityUnits,
    }),
    kind: YupEnum(Enums.QuoteProductKind, 'Kind'),
    discounts: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().default(''),
          importedDiscountId: Yup.string().default(''),
          name: Yup.string().default(''),
          amountType: Yup.string().default('fixed'),
          appliedAmount: Yup.string().default('0'),
        }),
      )
      .default([]),
    externalName: YupNullableString('External name'),
    class: YupNullableNumber({ label: 'Class' }),
    quantity: YupDecimal('Quantity').test({
      name: 'positive-quantity',
      message: 'Quantity must be greater than 0',
      test: (value) => Number(value) > 0,
    }),
    price: YupCurrency('Price').test(nonNegativeCurrency),
    originalPrice: YupNullableCurrency('Original price'),
    deliveryCosts: YupDeliveryCosts('Delivery costs'),
    usage: YupNullableString('Usage'),
    isMoneySwitchOn: Yup.bool().default(false),

    // Delivery Pricing-specific fields
    haulTaxable: Yup.bool().default(false),
    deliveryType: YupNullableEnum(Enums.ProductDeliveryType, 'Delivery type'),
    haulRate: YupNullableCurrency('Haul rate'),
    minimumHaulCharge: YupNullableNumber({ label: 'Minimum haul charge' }),
    taxRate: YupNullablePercentage('Tax rate', 2).test({
      name: 'taxRate-must-not-exceed-999.99',
      test: (item) => (item ?? '') === '' || parseFloat(item ?? '0') <= 999.99,
      message: 'Must not exceed 999.99',
    }),
    truckingType: YupNullableReference('TruckingType'),
    haulCost: YupNullableCurrency('Haul cost'),
    additionalTruckingInfos: AdditionalTruckingInfosSchema,
  }),
);

/**
 * AdditionalProjectOrQuoteProductsFormikSchema is the formik schema for the additional service
 * section of a Quote. It is used to validate the form data.
 * It operates properly for non-Primary products.
 */
export const AdditionalProjectOrQuoteProductsFormikSchema: Yup.SchemaOf<
  ProjectOrQuoteProductEditableFieldsFormikType[]
> = Yup.array().of(
  Yup.object({
    product: YupReference('Product', {
      incompatibleMixQuantityUnits: Yup.bool().default(false),
      allowIncompatibleMixQuantityUnits: Yup.bool().default(false),
      category: YupNullableEnum(Enums.ProductCategory, 'Category'),
      nonTaxable: Yup.bool().default(false),
    }).test({
      name: 'products have incompatible mix batch units',
      test: (item) => !item.incompatibleMixQuantityUnits || item.allowIncompatibleMixQuantityUnits,
    }),
    kind: YupEnum(Enums.QuoteProductKind, 'Kind'),
    discounts: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().default(''),
          importedDiscountId: Yup.string().default(''),
          name: Yup.string().default(''),
          amountType: Yup.string().default('fixed'),
          appliedAmount: Yup.string().default('0'),
        }),
      )
      .default([]),
    externalName: YupNullableString('External name'),
    class: YupNullableNumber({ label: 'Class' }),
    quantity: YupNullableDecimal('Quantity').test({
      name: 'positive-quantity',
      message: 'Quantity must be greater than 0',
      test: (value) => value === null || Number(value) > 0,
    }),
    price: YupCurrency('Price').test(nonNegativeCurrency),
    originalPrice: YupNullableCurrency('Original price'),
    deliveryCosts: YupDeliveryCosts('Delivery costs'),
    usage: YupNullableString('Usage'),
    isMoneySwitchOn: Yup.bool().default(false),

    // Delivery Pricing-specific fields
    haulTaxable: Yup.bool().default(false),
    deliveryType: YupNullableEnum(Enums.ProductDeliveryType, 'Delivery type'),
    haulRate: YupNullableCurrency('Haul rate'),
    minimumHaulCharge: YupNullableNumber({ label: 'Minimum haul charge' }),
    taxRate: YupNullablePercentage('Tax rate', 2).test({
      name: 'taxRate-must-not-exceed-999.99',
      test: (item) => (item ?? '') === '' || parseFloat(item ?? '0') <= 999.99,
      message: 'Must not exceed 999.99',
    }),
    truckingType: YupNullableReference('TruckingType').test({
      name: 'truckingType-required-when-delivery-type-is-hauling',
      test: (item, ctx) => {
        if (ctx.parent.deliveryType === Enums.ProductDeliveryType.Hauling) {
          return (item?.id ?? null) !== null;
        }
        return true;
      },
      message: 'Required',
    }),
    haulCost: YupNullableCurrency('Haul cost'),
    additionalTruckingInfos: AdditionalTruckingInfosSchema,
  }),
);

/** Returns the form data for a QuoteProduct's reference to its underlying Product. */
export const FormikProductReference = (
  product: Product,
): ProjectOrQuoteProductFormikType['product'] => {
  const labelPrefix = product.incompatibleMixQuantityUnits ? '[UOM issue] ' : '';
  const optionLabel = `${labelPrefix}${product.alternateID}`;
  return {
    id: product.id,
    // If you change this option data, you'll probably also want to make corresponding changes
    // to the lookup transform in ProductSection.
    option: {
      value: product.id,
      label: optionLabel,
      sublabels: [product.name],
    },
    incompatibleMixQuantityUnits: product.incompatibleMixQuantityUnits,
    allowIncompatibleMixQuantityUnits: false,
    category: product.category,
  };
};

export const buildAdditionalTruckingInfoFormik = (
  info: QuoteProductTruckingInfo,
  qpPrice: Currency,
  currencyCode?: string | null,
  nonTaxable = false,
): AdditionalTruckingInfoFormikType => {
  const taxRate = nonTaxable ? 0 : parseFloat(info.taxRate ?? '0') / 100;
  const haulRate = info.haulRate ?? Currency.zero();
  const withDeliverySubtotalPrice = haulRate.add(qpPrice);

  const withDeliveryTotalPrice = CalculateWithDeliveryTotalPrice({
    unitPrice: qpPrice,
    haulRate,
    taxRate,
    haulTaxable: info.haulTaxable,
  });

  const haulMargin = NewQuoteProduct({
    haulCost: info.haulCost,
    haulRate: info.haulRate,
  }).haulMarginAsPercentage();

  return {
    truckingType: {
      id: info.truckingType?.id ?? null,
      option:
        info.truckingType === null
          ? null
          : {
              value: info.truckingType.id,
              label: info.truckingType.name,
            },
    },
    haulRate: FormikCurrency(info.haulRate ?? undefined),
    minimumHaulCharge: info.minimumHaulCharge,
    taxRate: info.taxRate !== null ? parseFloat(info.taxRate).toFixed(2) : null,
    haulCost: FormikCurrency(info.haulCost ?? undefined),
    haulTaxable: info.haulTaxable ?? false,
    withDeliveryDisplayOnly: {
      withDeliverySubtotalPrice: NullableCurrencyString({
        cur: withDeliverySubtotalPrice,
        currencyCode,
      }),
      withDeliveryTotalPrice: NullableCurrencyString({
        cur: withDeliveryTotalPrice,
        currencyCode,
      }),
      haulMargin,
    },
  };
};

/** Returns the form data for a QuoteProduct. */
export const QuoteProductToProjectOrQuoteProductFormik = (
  qp: QuoteProduct,
  isMoneySwitchOn: boolean,
  marginConfiguration: Enums.MarginConfiguration = Enums.MarginConfiguration.Percentage,
  defaultMargins: TenantDefaultMargins = EmptyTenantDefaultMargins,
  projectProductPrice?: Currency,
): ProjectOrQuoteProductFormikType => {
  const productCategory = qp.product.category ?? Enums.ProductCategory.ReadyMix;
  const productReference = FormikProductReference(qp.product);
  const taxRate = qp.nonTaxable ? 0 : parseFloat(qp.taxRate ?? '0') / 100;
  const haulRate = qp.haulRate ?? Currency.zero();
  const withDeliverySubtotalPrice = haulRate.add(qp.price);
  const withDeliveryTotalPrice = CalculateWithDeliveryTotalPrice({
    unitPrice: qp.price,
    haulRate,
    taxRate,
    haulTaxable: qp.haulTaxable,
  });

  // Conditionally format target margin based on margin configuration
  const targetMargin = qp.product.targetMarginDisplay(marginConfiguration, defaultMargins);

  // Money display strings use the currency of the product's plant,
  // falling back to the tenant default.
  const currencyCode = resolveCurrencyCode(qp.product.plant.currencyCode);

  return {
    product: {
      ...productReference,
      category: productReference.category ?? productCategory,
      nonTaxable: qp.nonTaxable,
    },
    kind: qp.kind,
    discounts: qp.discounts,
    externalName: qp.externalName,
    class: qp.class,
    quantity: qp.quantity,
    price: FormikCurrency(qp.price),
    originalPrice: FormikCurrency(
      projectProductPrice ??
        qp.product.listPrice ??
        qp.calculateSuggestedPrice(marginConfiguration, defaultMargins) ??
        undefined,
    ),
    deliveryCosts: FormikDeliveryCosts(qp.deliveryCosts ?? undefined, currencyCode),
    isMoneySwitchOn,
    displayOnly: {
      suggestedPrice: NullableCurrencyString({
        cur: qp.calculateSuggestedPrice(marginConfiguration, defaultMargins),
        currencyCode,
      }),
      listPrice: NullableCurrencyString({ cur: qp.product.listPrice, currencyCode }),
      actualMarginPercentage: qp.marginAsPercentage(),
      targetMargin,
      targetMarginPercentage: qp.product.targetMarginDisplay(
        Enums.MarginConfiguration.Percentage,
        defaultMargins,
      ),
      targetMarginCurrency: CalculateTargetMarginCurrency({
        product: qp.product,
        totalCost: qp.costPerUnit().toUnit(),
        defaultMargins,
        currencyCode,
      }),
      unitCost: CurrencyString({ cur: qp.costPerUnit(), currencyCode }),
      actualMarginCurrency: qp.marginAsFormattedCurrency(currencyCode),
      marginOverMaterialsCurrency: qp.marginOverMaterialAsFormattedCurrency(currencyCode),
      marginOverMaterialsPercentage: qp.marginOverMaterialAsPercentage(),
    },
    usage: qp.usage,

    // Delivery Pricing-displayOnly fields
    withDeliveryDisplayOnly: {
      withDeliverySubtotalPrice: NullableCurrencyString({
        cur: withDeliverySubtotalPrice,
        currencyCode,
      }),

      withDeliveryTotalPrice: NullableCurrencyString({
        cur: withDeliveryTotalPrice,
        currencyCode,
      }),
      haulMargin: qp.haulMarginAsPercentage(),
    },

    // Delivery Pricing-specific fields
    haulTaxable: qp.haulTaxable,
    deliveryType: qp.deliveryType ?? null,
    haulRate: FormikCurrency(qp.haulRate ?? undefined),
    minimumHaulCharge: qp.minimumHaulCharge,
    taxRate: qp.taxRate !== null ? parseFloat(qp.taxRate).toFixed(2) : null,
    truckingType: {
      id: qp.truckingType?.id ?? null,
      option:
        qp.truckingType === null
          ? null
          : {
              value: qp.truckingType.id,
              label: qp.truckingType.name,
            },
    },
    haulCost: FormikCurrency(qp.haulCost ?? undefined),
    additionalTruckingInfos: (qp.additionalTruckingInfos ?? []).map((info) =>
      buildAdditionalTruckingInfoFormik(info, qp.price, currencyCode, qp.nonTaxable),
    ),
  };
};
